// Die Tage an der Anlage, in der Akte (E3, S5/S7):
//  - der Montagetermin, mit dem Kunden vereinbart (Gernots Entscheidung 4):
//    ein Tag und ein Text („Partie 2, 3 Tage“) – kein Kalender, keine
//    Partie-Planung (das ist die Kiste Termin);
//  - „Auftrag erteilt am“ und „montiert am“ für Manuels Zähler „verkauft –
//    montiert – Ziel“ (O58) – von Hand, bis die Kisten Sales und Montage sie
//    selbst eintragen.
// Je Zeile ein „ändern“: das Datumsfeld (und beim Termin der Text) an Ort und
// Stelle, „Speichern“, „Abbrechen“, und – ist schon etwas eingetragen – den
// Tag wieder leeren. Gespeichert wird über PATCH /api/crm/anlage/:id; nur, was
// mitkommt, ändert sich (Kern: anlageBerichtigenEingabe).

import { useState, type FormEvent, type ReactNode } from "react";
import { BUERO_GRENZEN, type AnlageBerichtigenEingabe, type BueroAntwort, type Montagetermin } from "@happy-energy/kern";
import { aendern, Ikon, toastZeigen } from "@happy-energy/oberflaeche";
import { kalendertagZeigen } from "../anzeige.ts";
import { meldungAus } from "../meldung.ts";
import { tagLesen } from "../tage.ts";

/** Schickt eine Änderung an der Anlage – null: gespeichert, sonst die Meldung. */
async function anlageAendern(anlageId: string, eingabe: AnlageBerichtigenEingabe): Promise<string | null> {
  try {
    await aendern<BueroAntwort>(`/api/crm/anlage/${anlageId}`, eingabe);
    return null;
  } catch (f) {
    return meldungAus(f);
  }
}

function Zeile({
  titel,
  ikon,
  wert,
  leer,
  bearbeiten,
  aendernKnopf,
  children,
}: {
  titel: string;
  ikon: "kalender" | "haken" | "werkzeug";
  wert: string | null;
  leer: string;
  bearbeiten: boolean;
  aendernKnopf: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`termin-zeile${bearbeiten ? " offen" : ""}`}>
      <span className="termin-zeichen">
        <Ikon name={ikon} />
      </span>
      <div className="termin-wort">
        <span className="termin-titel">{titel}</span>
        {!bearbeiten && <b className={wert ? undefined : "leer"}>{wert ?? leer}</b>}
        {bearbeiten && children}
      </div>
      {!bearbeiten && aendernKnopf}
    </div>
  );
}

function AendernKnopf({ an, leer, name }: { an: () => void; leer: boolean; name: string }) {
  return (
    <button type="button" className="crm-knopf rand klein" onClick={an} aria-label={`${name} ${leer ? "eintragen" : "ändern"}`}>
      <Ikon name="stift" groesse="k" /> {leer ? "eintragen" : "ändern"}
    </button>
  );
}

function Knoepfe({ sendet, abbrechen, leeren, leerenText }: { sendet: boolean; abbrechen: () => void; leeren: (() => void) | null; leerenText: string }) {
  return (
    <div className="crm-knopf-reihe">
      <button type="submit" className="crm-knopf lime klein" disabled={sendet}>
        {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Speichern
      </button>
      <button type="button" className="crm-knopf rand klein" onClick={abbrechen} disabled={sendet}>
        Abbrechen
      </button>
      {leeren && (
        <button type="button" className="crm-knopf leise klein" onClick={leeren} disabled={sendet}>
          {leerenText}
        </button>
      )}
    </div>
  );
}

/** Der Montagetermin: Tag und Text, „noch nicht vereinbart“, wenn leer. */
function MontageterminZeile({ anlageId, termin, gespeichert }: { anlageId: string; termin: Montagetermin; gespeichert: () => void }) {
  const [offen, setOffen] = useState(false);
  const [am, setAm] = useState("");
  const [text, setText] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const da = termin.am !== null || termin.text !== null;
  const anzeige = da ? [termin.am ? kalendertagZeigen(termin.am) : null, termin.text].filter(Boolean).join(" · ") : null;

  function oeffnen() {
    setAm(termin.am ?? "");
    setText(termin.text ?? "");
    setMeldung(null);
    setOffen(true);
  }

  async function schicken(neu: Montagetermin) {
    setSendet(true);
    const fehler = await anlageAendern(anlageId, { montagetermin: neu });
    setSendet(false);
    if (fehler) {
      setMeldung(fehler);
      return;
    }
    setOffen(false);
    toastZeigen(neu.am === null && neu.text === null ? "Montagetermin entfernt" : "Montagetermin gespeichert");
    gespeichert();
  }

  function speichern(e: FormEvent) {
    e.preventDefault();
    const tag = tagLesen(am);
    if (!tag.ok) {
      setMeldung(tag.meldung);
      return;
    }
    const t = text.trim();
    if (tag.tag === null && t === "") {
      setMeldung("Bitte einen Tag oder einen Text eingeben – oder „Termin entfernen“.");
      return;
    }
    void schicken({ am: tag.tag, text: t || null });
  }

  return (
    <Zeile
      titel="Montagetermin, mit dem Kunden vereinbart"
      ikon="kalender"
      wert={anzeige}
      leer="noch nicht vereinbart"
      bearbeiten={offen}
      aendernKnopf={<AendernKnopf an={oeffnen} leer={!da} name="Montagetermin" />}
    >
      <form
        className="termin-form"
        onSubmit={speichern}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !sendet) setOffen(false);
        }}
      >
        <div className="termin-felder">
          <label className="crm-feld">
            Tag
            <input className="crm-eingabe" type="date" value={am} autoFocus onChange={(e) => setAm(e.target.value)} />
          </label>
          <label className="crm-feld breit">
            <span>
              Dazu <span className="freiwillig">(z. B. „Partie 2, 3 Tage“)</span>
            </span>
            <input className="crm-eingabe" value={text} maxLength={BUERO_GRENZEN.montageterminText} onChange={(e) => setText(e.target.value)} />
          </label>
        </div>
        {meldung && (
          <p className="crm-fehler-satz" role="alert">
            {meldung}
          </p>
        )}
        <Knoepfe sendet={sendet} abbrechen={() => setOffen(false)} leeren={da ? () => void schicken({ am: null, text: null }) : null} leerenText="Termin entfernen" />
      </form>
    </Zeile>
  );
}

/** „Auftrag erteilt am“ oder „montiert am“ – ein Tag, leeren möglich. */
function TagZeile({
  anlageId,
  feld,
  titel,
  ikon,
  tag,
  gespeichert,
}: {
  anlageId: string;
  feld: "auftragAm" | "montiertAm";
  titel: string;
  ikon: "haken" | "werkzeug";
  tag: string | null;
  gespeichert: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const [wert, setWert] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  function oeffnen() {
    setWert(tag ?? "");
    setMeldung(null);
    setOffen(true);
  }

  async function schicken(neu: string | null) {
    setSendet(true);
    const fehler = await anlageAendern(anlageId, feld === "auftragAm" ? { auftragAm: neu } : { montiertAm: neu });
    setSendet(false);
    if (fehler) {
      setMeldung(fehler);
      return;
    }
    setOffen(false);
    toastZeigen(`„${titel}“ ${neu === null ? "geleert" : "gespeichert"}`);
    gespeichert();
  }

  function speichern(e: FormEvent) {
    e.preventDefault();
    const gelesen = tagLesen(wert);
    if (!gelesen.ok) {
      setMeldung(gelesen.meldung);
      return;
    }
    if (gelesen.tag === tag) {
      setOffen(false);
      return;
    }
    void schicken(gelesen.tag);
  }

  return (
    <Zeile
      titel={titel}
      ikon={ikon}
      wert={tag ? kalendertagZeigen(tag) : null}
      leer="–"
      bearbeiten={offen}
      aendernKnopf={<AendernKnopf an={oeffnen} leer={tag === null} name={titel} />}
    >
      <form
        className="termin-form"
        onSubmit={speichern}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !sendet) setOffen(false);
        }}
      >
        <div className="termin-felder">
          <label className="crm-feld">
            Tag
            <input className="crm-eingabe" type="date" value={wert} autoFocus onChange={(e) => setWert(e.target.value)} />
          </label>
        </div>
        {meldung && (
          <p className="crm-fehler-satz" role="alert">
            {meldung}
          </p>
        )}
        <Knoepfe sendet={sendet} abbrechen={() => setOffen(false)} leeren={tag !== null ? () => void schicken(null) : null} leerenText="Tag leeren" />
      </form>
    </Zeile>
  );
}

/** Die Karte „Termine der Anlage“ in der Akte. */
export function TermineKarte({
  anlage,
  gespeichert,
}: {
  anlage: { id: string; montagetermin: Montagetermin; auftragAm: string | null; montiertAm: string | null };
  gespeichert: () => void;
}) {
  return (
    <section id="akte-termin" className="crm-karte termine-karte" aria-label="Termine der Anlage">
      <div className="karten-kopf">
        <Ikon name="kalender" />
        <h2>Termine der Anlage</h2>
      </div>
      <MontageterminZeile anlageId={anlage.id} termin={anlage.montagetermin} gespeichert={gespeichert} />
      <TagZeile anlageId={anlage.id} feld="auftragAm" titel="Auftrag erteilt am" ikon="haken" tag={anlage.auftragAm} gespeichert={gespeichert} />
      <TagZeile anlageId={anlage.id} feld="montiertAm" titel="Montiert am" ikon="werkzeug" tag={anlage.montiertAm} gespeichert={gespeichert} />
      <p className="leise klein termine-hinweis">
        <Ikon name="info" groesse="k" /> „Auftrag erteilt am“ und „montiert am“ von Hand, bis die Kisten Sales und Montage es selbst eintragen – daraus zählt
        die Übersicht „verkauft – montiert – Ziel“.
      </p>
    </section>
  );
}
